import { useCallback, useEffect, useRef, useState } from "react";

import { blobToWav } from "./wav";

export const VOICE_MAX_SECONDS = 120;
const MIN_CLIP_MS = 400;

const pickMimeType = () => {
  if (typeof MediaRecorder === "undefined" || !MediaRecorder.isTypeSupported) {
    return "";
  }
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4",
    "audio/ogg;codecs=opus",
  ];
  return candidates.find((type) => MediaRecorder.isTypeSupported(type)) || "";
};

// Push-to-talk recorder state machine. The composer owns the transcript via
// onTranscript; this hook only produces a normalized WAV blob per clip.
const useVoiceInput = ({ onTranscript, transcribe, enabled = true } = {}) => {
  const [status, setStatus] = useState("idle"); // idle | recording | transcribing
  const [elapsedMs, setElapsedMs] = useState(0);
  const [error, setError] = useState("");

  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const startedAtRef = useRef(0);
  const timerRef = useRef(null);
  const maxTimerRef = useRef(null);
  const abortRef = useRef(null);
  const cancelledRef = useRef(false);
  const statusRef = useRef("idle");
  statusRef.current = status;
  /* Synchronous start-in-progress flag: getUserMedia is async, so two rapid
     start() calls (chord timer + mouse click within the arm delay) would
     both pass the idle check and leave an orphaned MediaRecorder holding
     the microphone. */
  const startingRef = useRef(false);

  const supported =
    typeof navigator !== "undefined" &&
    Boolean(navigator.mediaDevices && navigator.mediaDevices.getUserMedia) &&
    typeof MediaRecorder !== "undefined";

  const clearTimers = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (maxTimerRef.current) {
      clearTimeout(maxTimerRef.current);
      maxTimerRef.current = null;
    }
  }, []);

  const releaseStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, []);

  const finishRecording = useCallback(
    async (blob, durationMs) => {
      if (cancelledRef.current || durationMs < MIN_CLIP_MS || !blob || blob.size === 0) {
        setStatus("idle");
        setElapsedMs(0);
        return;
      }
      setStatus("transcribing");
      try {
        const wavBlob = await blobToWav(blob);
        abortRef.current = new AbortController();
        const text = await transcribe({
          blob: wavBlob,
          signal: abortRef.current.signal,
        });
        const transcript = typeof text === "string" ? text.trim() : "";
        if (transcript && typeof onTranscript === "function") {
          onTranscript(transcript);
        } else if (!transcript) {
          setError("No speech detected. Please try again.");
        }
      } catch (err) {
        if (!err || err.name !== "AbortError") {
          const detail =
            (err && err.response && err.response.data && err.response.data.detail) ||
            (err && err.message) ||
            "Transcription failed.";
          setError(detail);
        }
      } finally {
        abortRef.current = null;
        setStatus("idle");
        setElapsedMs(0);
      }
    },
    [onTranscript, transcribe]
  );

  const stop = useCallback(() => {
    if (statusRef.current !== "recording") return;
    clearTimers();
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.stop();
    }
  }, [clearTimers]);

  const cancel = useCallback(() => {
    cancelledRef.current = true;
    clearTimers();
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.stop();
    } else {
      releaseStream();
      setStatus("idle");
      setElapsedMs(0);
    }
    if (abortRef.current) {
      abortRef.current.abort();
    }
  }, [clearTimers, releaseStream]);

  const start = useCallback(async () => {
    if (!enabled || !supported || startingRef.current || statusRef.current !== "idle") return;
    startingRef.current = true;
    setError("");
    cancelledRef.current = false;

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
    } catch (err) {
      startingRef.current = false;
      if (!cancelledRef.current) {
        setError(
          err && (err.name === "NotAllowedError" || err.name === "SecurityError")
            ? "Microphone access was denied."
            : "Microphone is unavailable."
        );
      }
      return;
    }

    /* Esc pressed during the getUserMedia latency window: cancel() already
       flagged this attempt, but no recorder existed yet, so its cleanup was
       a no-op on the stream this continuation has just acquired. Stop those
       tracks and bail BEFORE creating the recorder — otherwise the mic keeps
       capturing as an orphaned "Recording Ns" run the user already
       cancelled. Status stays whatever cancel() left it (idle). */
    if (cancelledRef.current) {
      if (stream && typeof stream.getTracks === "function") {
        stream.getTracks().forEach((track) => track.stop());
      }
      streamRef.current = null;
      startingRef.current = false;
      return;
    }

    streamRef.current = stream;
    chunksRef.current = [];
    const mimeType = pickMimeType();
    const recorder = mimeType
      ? new MediaRecorder(stream, { mimeType })
      : new MediaRecorder(stream);
    recorderRef.current = recorder;
    startedAtRef.current = Date.now();

    recorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) {
        chunksRef.current.push(event.data);
      }
    };
    recorder.onstop = () => {
      const durationMs = Date.now() - startedAtRef.current;
      const blob = new Blob(chunksRef.current, {
        type: recorder.mimeType || mimeType || "audio/webm",
      });
      chunksRef.current = [];
      recorderRef.current = null;
      releaseStream();
      finishRecording(blob, durationMs);
    };

    try {
      recorder.start();
    } catch {
      startingRef.current = false;
      recorderRef.current = null;
      releaseStream();
      setError("Microphone is unavailable.");
      return;
    }
    // Recording is live: mirror the status synchronously so the entry guard
    // holds even before the re-render assigns statusRef, then release the
    // start lock.
    setStatus("recording");
    statusRef.current = "recording";
    startingRef.current = false;
    setElapsedMs(0);
    timerRef.current = setInterval(() => {
      setElapsedMs(Date.now() - startedAtRef.current);
    }, 200);
    maxTimerRef.current = setTimeout(() => {
      stop();
    }, VOICE_MAX_SECONDS * 1000);
  }, [enabled, supported, finishRecording, releaseStream, stop]);

  // Cancel an in-flight recording when the window loses focus or the component
  // unmounts, so the microphone indicator never stays on unexpectedly.
  useEffect(() => {
    const handleBlur = () => {
      if (statusRef.current === "recording") {
        cancel();
      }
    };
    window.addEventListener("blur", handleBlur);
    return () => {
      window.removeEventListener("blur", handleBlur);
      cancelledRef.current = true;
      clearTimers();
      const recorder = recorderRef.current;
      if (recorder && recorder.state !== "inactive") {
        recorder.stop();
      }
      releaseStream();
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
  }, [cancel, clearTimers, releaseStream]);

  return {
    status,
    elapsedMs,
    error,
    supported,
    start,
    stop,
    cancel,
    clearError: useCallback(() => setError(""), []),
  };
};

export default useVoiceInput;
